/**
 * Marquee infinito Andrade — loop contínuo (desktop e mobile).
 */
(function () {
  /** @type {WeakSet<HTMLElement>} */
  const observedTracks = new WeakSet();

  /**
   * @param {HTMLElement} track
   */
  function setupInfiniteLoop(track) {
    if (!track) return;

    const groupSelector = track.dataset.marqueeGroup || '.promo-trust-bar__group';
    const viewport = track.parentElement;
    const group = track.querySelector(`${groupSelector}:not([data-andrade-loop-clone])`);
    if (!group || !viewport) return;
    if (observedTracks.has(track)) return;
    observedTracks.add(track);

    /** @type {number | null} */
    let debounceTimer = null;
    /** @type {number | null} */
    let retryTimer = null;
    let retryCount = 0;
    let lastShift = 0;
    let lastCopies = 0;
    /** @type {number} */
    let rafId = 0;
    let offsetPx = 0;
    let lastTs = 0;
    let usingJsFallback = false;

    const stopJsTicker = () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = 0;
      }
      usingJsFallback = false;
      lastTs = 0;
    };

    const isPhoneViewport = () => window.matchMedia('(max-width: 749px)').matches;

    const startJsTicker = (shiftPx, durationSec) => {
      if (shiftPx <= 0) return;
      stopJsTicker();
      usingJsFallback = true;
      track.classList.remove('is-animating');
      track.style.webkitAnimation = 'none';
      track.style.animation = 'none';
      const speed = shiftPx / Math.max(durationSec, 4);

      const tick = (ts) => {
        if (!usingJsFallback) return;
        if (!lastTs) lastTs = ts;
        const dt = Math.min((ts - lastTs) / 1000, 0.05);
        lastTs = ts;
        offsetPx = (offsetPx + speed * dt) % shiftPx;
        const next = `translate3d(${-offsetPx}px, 0, 0)`;
        track.style.webkitTransform = next;
        track.style.transform = next;
        rafId = window.requestAnimationFrame(tick);
      };

      rafId = window.requestAnimationFrame(tick);
    };

    const cssAnimationStuck = () => {
      const style = window.getComputedStyle(track);
      const anim = style.animationName || style.webkitAnimationName;
      if (!anim || anim === 'none') return true;
      const durationMs = parseFloat(style.animationDuration) * 1000;
      if (durationMs > 0 && durationMs < 50) return true;
      return false;
    };

    const syncClones = () => {
      track.querySelectorAll('[data-andrade-loop-clone]').forEach((node) => node.remove());

      const groupWidth = Math.ceil(group.getBoundingClientRect().width);
      const viewWidth = Math.ceil(viewport.getBoundingClientRect().width);
      if (!groupWidth || !viewWidth) return null;

      const minCopies = Math.max(3, Math.ceil((viewWidth + groupWidth) / groupWidth) + 1);

      for (let i = 1; i < minCopies; i += 1) {
        const clone = group.cloneNode(true);
        clone.setAttribute('data-andrade-loop-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      }

      return { groupWidth, minCopies };
    };

    const scheduleRetry = () => {
      if (lastShift > 0) return;
      if (retryCount >= 20) return;

      retryCount += 1;
      if (retryTimer) window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        window.requestAnimationFrame(applyMetrics);
      }, Math.min(120 * retryCount, 900));
    };

    const applyMetrics = () => {
      const metrics = syncClones();
      if (!metrics) {
        scheduleRetry();
        return;
      }

      const { groupWidth, minCopies } = metrics;
      const changed = groupWidth !== lastShift || minCopies !== lastCopies;
      lastShift = groupWidth;
      lastCopies = minCopies;

      track.style.setProperty('--marquee-shift', `${groupWidth}px`);

      const seconds = parseFloat(track.dataset.speed || '16');
      const isPhone = isPhoneViewport();
      const duration = isPhone
        ? Math.min(Math.max(seconds * 0.22, 8), 12)
        : Math.max(seconds, 4);
      track.style.setProperty('--marquee-duration', `${duration}s`);
      track.style.width = `${groupWidth * minCopies}px`;

      if (!changed && lastShift > 0 && (usingJsFallback || track.classList.contains('is-animating'))) {
        return;
      }

      stopJsTicker();
      offsetPx = 0;
      track.style.webkitTransform = '';
      track.style.transform = '';
      track.style.webkitAnimation = '';
      track.style.animation = '';
      track.classList.remove('is-animating');
      void track.offsetWidth;

      if (isPhone) {
        startJsTicker(groupWidth, duration);
        return;
      }

      track.classList.add('is-animating');

      window.setTimeout(() => {
        if (lastShift <= 0) return;
        if (document.documentElement.classList.contains('shopify-design-mode')) return;
        if (cssAnimationStuck()) {
          startJsTicker(groupWidth, duration);
        }
      }, 400);
    };

    const scheduleApply = () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        window.requestAnimationFrame(applyMetrics);
      }, 80);
    };

    applyMetrics();
    scheduleRetry();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(scheduleApply);
      observer.observe(group);
      observer.observe(viewport);
    } else {
      window.addEventListener('resize', scheduleApply);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleApply).catch(() => {});
    }

    track.querySelectorAll('img, video').forEach((media) => {
      if (!(media instanceof HTMLImageElement || media instanceof HTMLVideoElement)) return;
      if (media instanceof HTMLImageElement && media.complete) return;
      media.addEventListener('loadeddata', scheduleApply, { once: true });
      media.addEventListener('load', scheduleApply, { once: true });
    });

    if (typeof IntersectionObserver !== 'undefined') {
      const visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) scheduleApply();
          });
        },
        { threshold: 0.01 }
      );
      visibilityObserver.observe(track);
    }

    window.addEventListener('pageshow', scheduleApply);
    window.addEventListener('orientationchange', () => {
      window.setTimeout(scheduleApply, 200);
    });
  }

  /**
   * @param {ParentNode} [root]
   */
  function initAll(root = document) {
    root.querySelectorAll('[data-andrade-infinite-marquee]').forEach(setupInfiniteLoop);
  }

  function boot() {
    if (document.documentElement.classList.contains('shopify-design-mode')) return;
    initAll();
    window.addEventListener('load', () => initAll());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (document.documentElement.classList.contains('shopify-design-mode')) return;
    if (event.target instanceof HTMLElement) {
      initAll(event.target);
    }
  });
})();
